import React from 'react';

// [2026-10-27] 기프트 등록·수정 — 쿠폰 적용 허용(Y)일 때 이 기프트에 쓸 쿠폰을 고르는 체크박스 목록.
// candidates: GET gift/available-coupons/:storeNo 응답 / selected: 고른 promotionNo 배열
// endedSelected: 수정 화면에서 이미 골라져 있지만 종료·중지돼 후보에 없는 쿠폰(상세 응답 couponPromotions 중 ended)

export const ISSUER_LABEL = { STORE: '상점 프로모션', CHANNEL: '채널 프로모션', PLATFORM: '내기프트 프로모션' };
// [2026-10-28] 발행처별 배지 색 [배경, 글자] — 상점 초록 · 채널 남색 · 내기프트 주황
const ISSUER_TONE = {
  STORE: ['#e8f7ee', '#18794e'],
  CHANNEL: ['#e8eefb', '#1e3a8a'],
  PLATFORM: ['#fff1ea', '#c2410c'],
};

const formatDate = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(d)
    .replace(/\s/g, '')
    .replace(/\.$/, '');
};

const formatPeriod = (start, end) => {
  const s = formatDate(start);
  const e = formatDate(end);
  if (!s && !e) return '기간 제한 없음';
  return `${s || ''} ~ ${e || '종료일 없음'}`;
};

const badgeStyle = (bg, color) => ({
  display: 'inline-block',
  padding: '1px 6px',
  marginRight: '4px',
  borderRadius: '4px',
  fontSize: '11px',
  lineHeight: '16px',
  background: bg,
  color,
});

export default function GiftCouponPicker({
  candidates = [],
  selected = [],
  onChange,
  endedSelected = [],
  loaded = true,
  disabled = false,
  error = '',
}) {
  const selectedSet = new Set(selected.map(Number));
  const candidateNos = candidates.map(c => Number(c.promotionNo));
  const allChecked = candidateNos.length > 0 && candidateNos.every(no => selectedSet.has(no));

  const toggle = no => {
    if (disabled) return;
    const next = selectedSet.has(no)
      ? selected.filter(v => Number(v) !== no)
      : [...selected, no];
    onChange(next.map(Number));
  };

  const toggleAll = () => {
    if (disabled) return;
    if (allChecked) {
      onChange(selected.map(Number).filter(no => !candidateNos.includes(no)));
    } else {
      onChange([...new Set([...selected.map(Number), ...candidateNos])]);
    }
  };

  if (!loaded) {
    return <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>쿠폰 목록을 불러오는 중입니다…</div>;
  }

  if (!candidates.length && !endedSelected.length) {
    return (
      <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
        이 상점에서 선택할 수 있는 쿠폰이 없습니다.
      </div>
    );
  }

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 4px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '13px',
    cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ border: '1px solid #ddd', borderRadius: '6px', maxHeight: '240px', overflowY: 'auto', padding: '2px 8px' }}>
        {candidates.length > 0 && (
          <label style={{ ...rowStyle, fontWeight: 600 }}>
            <input type="checkbox" checked={allChecked} disabled={disabled} onChange={toggleAll} />
            전체 선택
          </label>
        )}
        {candidates.map(c => {
          const no = Number(c.promotionNo);
          return (
            <label key={no} style={rowStyle}>
              <input
                type="checkbox"
                checked={selectedSet.has(no)}
                disabled={disabled}
                onChange={() => toggle(no)}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={badgeStyle(...(ISSUER_TONE[c.issuerType] || ISSUER_TONE.STORE))}>{ISSUER_LABEL[c.issuerType] || c.issuerType}</span>
                {c.missRewardYn === 'Y' && <span style={badgeStyle('#fff1e6', '#b45309')}>꽝보상</span>}
                {c.hasCouponCode && <span style={badgeStyle('#f3f0ff', '#5b3fb8')}>코드입력</span>}
                {c.name}
                <span style={{ marginLeft: '6px', color: '#888', fontSize: '12px' }}>
                  {Number(c.couponDiscountAmount || 0).toLocaleString()}원 할인 · {formatPeriod(c.startDttm, c.endDttm)}
                </span>
              </span>
            </label>
          );
        })}
        {endedSelected.map(c => {
          const no = Number(c.promotionNo);
          return (
            <label key={`ended-${no}`} style={{ ...rowStyle, color: '#aaa' }}>
              <input
                type="checkbox"
                checked={selectedSet.has(no)}
                disabled={disabled}
                onChange={() => toggle(no)}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                {/* [2026-10-30] 중지(DISABLED)와 종료(ENDED·기간 지남)를 구분 */}
                {c.name} ({c.status === 'DISABLED' ? '중지' : '종료'})
              </span>
            </label>
          );
        })}
      </div>
      {/* [2026-10-28] 0개면 저장이 막히므로 저장 누르기 전에 빨간 글씨로 미리 알린다 */}
      <div
        style={{
          marginTop: '6px',
          fontSize: '12px',
          color: error || selectedSet.size === 0 ? '#d93025' : '#888',
          fontWeight: error || selectedSet.size === 0 ? 600 : 400,
        }}
      >
        {error ||
          (selectedSet.size === 0
            ? '쿠폰을 1개 이상 골라 주세요. 0개면 저장할 수 없습니다.'
            : `선택한 쿠폰만 이 기프트 결제에 쓸 수 있습니다. (${selectedSet.size}개 선택)`)}
      </div>
    </div>
  );
}
